import { type AutoRefreshConfig } from './model/adServerTypes'

export const AD_BANNER_STATUS_DATA_ATTR = 'data-as-status'
export const AD_BANNER_LOCAL_STORAGE_USER_CLOSED_FLAG_KEY = 'ad_banner_flag'
export const AD_SERVER_MEDIA_CODE_SEARCH_PARAM = 'mediacode'
export const AD_SERVER_XSELL_PRODUCT_ID_DATA_ATTR = 'data-as-xsell-product-id'
export const AD_SERVER_VISIBLE_EVENT_TYPE = 30

// 90 days in seconds
export const AD_SERVER_BROWSER_ID_CONVERSION_COOKIE_MAX_AGE = 7_776_000
// 7 days in seconds
export const AD_SERVER_BROWSER_ID_TARGETING_COOKIE_MAX_AGE = 604_800

export const AD_BANNER_WIDTH_DATA_ATTR = 'data-as-width'
export const AD_BANNER_HEIGHT_DATA_ATTR = 'data-as-height'
export const AD_BANNER_TAGS_DATA_ATTR = 'data-as-tags'
export const AD_BANNER_AUTO_REFRESH_DATA_ATTR = 'data-as-auto-refresh'
export const AD_BANNER_TOP_BANNER = 'top-banner'
export const AD_BANNER_PDP_DEFAULT = 'pds-b1'
// product page: RX message
export const AD_BANNER_PDP_RX = 'pds-b1-rx'
// cart intermediate banner
export const AD_BANNER_CART_INTERMEDIATE = 'wkzs-b1'

export const AD_BANNER_ORDER_CONFIRMATION = 'ty-b1'

// 1 day in seconds
export const AD_SERVER_CAMPAIGN_CODE_EXPIRY_IN_MS = 86_400_000

/**
 * We use `ad-exp-` prefix to introduce new experiments on all ad products (banners, xsell and search) without changing the code.
 * By using this prefix we get the ad-server related experiments and use them for:
 * 1. Activate the experiment on the optimizely
 * 2. Pass the experiment name and variant (as a part of the ad-server context)
 * to the ad-server to display the banner for the specific experiment variant
 *
 * Some more information how to set up the ad-server experiments:
 * https://wiki.shop-apotheke.com/pages/viewpage.action?pageId=335087751
 */
export const AD_SERVER_EXPERIMENTS_PREFIX = 'ad-exp-'

export type AdServerAdBannerDimensions = {
  height: number
  width: number
}

export type AdServerAdBannerSizes = {
  large: AdServerAdBannerDimensions
  small: AdServerAdBannerDimensions
}

export const AD_BANNER_PDP_DEFAULT_DIMENSIONS: AdServerAdBannerSizes = {
  large: { height: 200, width: 480 },
  small: { height: 135, width: 347 },
}

export const AD_BANNER_PDP_RX_DIMENSIONS: AdServerAdBannerSizes = {
  large: { height: 210, width: 480 },
  small: { height: 140, width: 347 },
}

export const AD_BANNER_CART_INTERMEDIATE_DIMENSIONS: AdServerAdBannerSizes = {
  large: { height: 150, width: 357 },
  small: { height: 135, width: 347 },
}

export const AD_BANNER_ORDER_CONFIRMATION_DIMENSIONS: AdServerAdBannerSizes = {
  large: { height: 300, width: 980 },
  small: { height: 135, width: 347 },
}

export const AD_BANNER_AUTO_REFRESH_CONFIG: AutoRefreshConfig = {
  intervalMs: 10_000,
  maxCount: 10,
}

// Used in Storyblok category pages
export const AD_BANNER_CATEGORY_WIDE = 'cp-b1'
export const AD_BANNER_CATEGORY_WIDE_DIMENSIONS = {
  LARGE: { height: 220, width: 728 },
  SMALL: { height: 135, width: 347 },
}

export const AD_BANNER_CATEGORY_SMALL_1 = 'cp-b2'
export const AD_BANNER_CATEGORY_SMALL_2 = 'cp-b3'

export const AD_BANNER_CATEGORY_SMALL_DIMENSIONS = {
  LARGE: { height: 150, width: 354 },
  SMALL: { height: 135, width: 347 },
}

const AD_BANNER_HOMEPAGE_WIDE = 'hp-b1'
export const AD_BANNER_SKYSCRAPER_LEFT = 'hp-sb1'
export const AD_BANNER_SKYSCRAPER_RIGHT = 'hp-sb2'
export const AD_BANNER_SKYSCRAPER_DIMENSIONS = {
  height: 600,
  width: 160,
}

export const AD_BANNER_AUTO_REFRESH_IDS: string[] = [
  AD_BANNER_HOMEPAGE_WIDE,
  AD_BANNER_CATEGORY_WIDE,
  AD_BANNER_PDP_DEFAULT,
  AD_BANNER_PDP_RX,
]

export const AD_BANNER_AUTO_REFRESH_PAUSE_EVENT_NAME = 'as-banner-pause'
export const AD_BANNER_AUTO_REFRESH_RESUME_EVENT_NAME = 'as-banner-resume'
export const AD_BANNER_AUTO_REFRESH_PREV_EVENT_NAME = 'as-banner-prev'
export const AD_BANNER_AUTO_REFRESH_NEXT_EVENT_NAME = 'as-banner-next'
export const AD_BANNER_AUTO_REFRESH_SLIDE_ID = 'as-banner-slide'
export const AD_BANNER_PLACEHOLDER_CLASS_NAME = 'as-placeholder'
